import {
  useEffect,
  useMemo,
  useState,
} from "react";

import { jsPDF } from "jspdf";

import Api from "../../../../shared/api/api";

import developmentLogo from "../../assets/AvwakinDevelopmentLogo.png";

import "../styles/DevelopmentPdfPreview.css";

const PAGE_WIDTH = 612;
const PAGE_HEIGHT = 792;

const PAGE_MARGIN = 48;

const CONTENT_WIDTH =
  PAGE_WIDTH - PAGE_MARGIN * 2;

const COLORS = {
  headerBackground: [
    190,
    196,
    204,
  ],

  navy: [
    16,
    52,
    82,
  ],

  subText: [
    54,
    76,
    96,
  ],

  blue: [
    23,
    111,
    176,
  ],

  muted: [
    95,
    110,
    123,
  ],

  border: [
    210,
    219,
    226,
  ],

  light: [
    244,
    248,
    251,
  ],

  success: [
    34,
    126,
    83,
  ],

  danger: [
    177,
    48,
    48,
  ],

  white: [
    255,
    255,
    255,
  ],

  black: [
    20,
    25,
    30,
  ],
};

function safeString(value) {
  if (
    value === null ||
    value === undefined
  ) {
    return "";
  }

  return String(value);
}

function cleanText(value) {
  return safeString(value).trim();
}

function parseDocumentDetails(value) {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return {};
  }

  if (
    typeof value === "object" &&
    !Array.isArray(value)
  ) {
    return value;
  }

  try {
    const parsed =
      JSON.parse(
        safeString(value)
      );

    if (
      parsed &&
      typeof parsed === "object" &&
      !Array.isArray(parsed)
    ) {
      return parsed;
    }
  } catch (error) {
    console.warn(
      "Document details could not be parsed:",
      error
    );
  }

  return {};
}

function firstAvailableText(
  ...values
) {
  for (const value of values) {
    const cleaned =
      cleanText(value);

    if (cleaned) {
      return cleaned;
    }
  }

  return "";
}

function formatCurrency(value) {
  const amount = Number(value);

  return new Intl.NumberFormat(
    "en-US",
    {
      style: "currency",
      currency: "USD",
    }
  ).format(
    Number.isFinite(amount)
      ? amount
      : 0
  );
}

function sumItemTotals(
  items = []
) {
  return items.reduce(
    (total, item) =>
      total +
      (
        Number(
          item?.line_total
        ) || 0
      ),
    0
  );
}

function normalizePercentage(
  value
) {
  const percentage =
    Number(value);

  if (
    !Number.isFinite(
      percentage
    ) ||
    percentage < 0 ||
    percentage > 100
  ) {
    return 0;
  }

  return percentage;
}

function formatPercentage(
  value
) {
  const percentage =
    normalizePercentage(
      value
    );

  return `${percentage.toLocaleString(
    "en-US",
    {
      maximumFractionDigits:
        2,
    }
  )}%`;
}

function formatTotalValue(
  entry
) {
  if (
    entry.formattedValue !==
      undefined &&
    entry.formattedValue !==
      null
  ) {
    return safeString(
      entry.formattedValue
    );
  }

  return formatCurrency(
    entry.value
  );
}

function normalizeDate(value) {
  return value
    ? safeString(value).slice(
        0,
        10
      )
    : "";
}

function formatDate(value) {
  if (!value) {
    return "—";
  }

  const normalized =
    normalizeDate(value);

  const date = new Date(
    `${normalized}T00:00:00`
  );

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return "—";
  }

  return new Intl.DateTimeFormat(
    "en-US",
    {
      month: "long",
      day: "numeric",
      year: "numeric",
    }
  ).format(date);
}

function formatLabel(value) {
  return safeString(value)
    .replaceAll("_", " ")
    .replace(
      /\b\w/g,
      (letter) =>
        letter.toUpperCase()
    );
}

function getClientName(client) {
  if (!client) {
    return "Client";
  }

  const personalName = [
    client.first_name,
    client.last_name,
  ]
    .filter(Boolean)
    .join(" ")
    .trim();

  return (
    client.business_name ||
    personalName ||
    `Client ${
      client.id || ""
    }`.trim()
  );
}

function sanitizeFileName(
  value
) {
  const sanitized =
    safeString(value)
      .trim()
      .replaceAll("\\", "/")
      .split("/")
      .pop()
      .replace(
        /[^a-zA-Z0-9._-]+/g,
        "-"
      )
      .replace(
        /-+/g,
        "-"
      )
      .replace(
        /^[-.]+|[-.]+$/g,
        ""
      ) ||
    "development-document";

  return sanitized
    .toLowerCase()
    .endsWith(".pdf")
    ? sanitized
    : `${sanitized}.pdf`;
}

function arrayBufferToBase64(
  arrayBuffer
) {
  const bytes =
    new Uint8Array(
      arrayBuffer
    );

  let binary = "";

  const chunkSize =
    0x8000;

  for (
    let index = 0;
    index < bytes.length;
    index += chunkSize
  ) {
    const chunk =
      bytes.subarray(
        index,
        Math.min(
          index + chunkSize,
          bytes.length
        )
      );

    binary +=
      String.fromCharCode(
        ...chunk
      );
  }

  return window.btoa(
    binary
  );
}

async function imageUrlToDataUrl(
  imageUrl
) {
  const response =
    await fetch(imageUrl);

  if (!response.ok) {
    throw new Error(
      "The Avwakin Development logo could not be loaded."
    );
  }

  const blob =
    await response.blob();

  return new Promise(
    (resolve, reject) => {
      const reader =
        new FileReader();

      reader.onloadend = () =>
        resolve(
          reader.result
        );

      reader.onerror = () =>
        reject(
          new Error(
            "The logo could not be converted for the PDF."
          )
        );

      reader.readAsDataURL(
        blob
      );
    }
  );
}

function splitText(
  pdf,
  text,
  width
) {
  const normalized =
    cleanText(text);

  if (!normalized) {
    return [];
  }

  return pdf.splitTextToSize(
    normalized,
    width
  );
}

function createPageManager(
  pdf
) {
  let y = PAGE_MARGIN;

  const ensureSpace = (
    requiredHeight,
    onNewPage
  ) => {
    if (
      y + requiredHeight <=
      PAGE_HEIGHT -
        PAGE_MARGIN
    ) {
      return;
    }

    pdf.addPage();

    y = PAGE_MARGIN;

    if (
      typeof onNewPage ===
      "function"
    ) {
      onNewPage();
    }
  };

  return {
    getY() {
      return y;
    },

    setY(value) {
      y = value;
    },

    move(amount) {
      y += amount;
    },

    ensureSpace,
  };
}

function addPageFooter(
  pdf,
  pageNumber
) {
  pdf.setDrawColor(
    ...COLORS.border
  );

  pdf.line(
    PAGE_MARGIN,
    PAGE_HEIGHT - 34,
    PAGE_WIDTH -
      PAGE_MARGIN,
    PAGE_HEIGHT - 34
  );

  pdf.setFont(
    "helvetica",
    "normal"
  );

  pdf.setFontSize(8);

  pdf.setTextColor(
    ...COLORS.muted
  );

  pdf.text(
    "Avwakin Development",
    PAGE_MARGIN,
    PAGE_HEIGHT - 19
  );

  pdf.text(
    `Page ${pageNumber}`,
    PAGE_WIDTH -
      PAGE_MARGIN,
    PAGE_HEIGHT - 19,
    {
      align: "right",
    }
  );
}

function addAllPageFooters(
  pdf
) {
  const pageCount =
    pdf.getNumberOfPages();

  for (
    let pageNumber = 1;
    pageNumber <=
    pageCount;
    pageNumber += 1
  ) {
    pdf.setPage(
      pageNumber
    );

    addPageFooter(
      pdf,
      pageNumber
    );
  }
}

function addDocumentHeader(
  pdf,
  logoDataUrl,
  document,
  client,
  project
) {
  pdf.setFillColor(
    ...COLORS.headerBackground
  );

  pdf.rect(
    0,
    0,
    PAGE_WIDTH,
    112,
    "F"
  );

  if (logoDataUrl) {
    pdf.addImage(
      logoDataUrl,
      "PNG",
      PAGE_MARGIN,
      20,
      72,
      72
    );
  }

  pdf.setTextColor(
    ...COLORS.navy
  );

  pdf.setFont(
    "helvetica",
    "bold"
  );

  pdf.setFontSize(18);

  pdf.text(
    "AVWAKIN DEVELOPMENT",
    136,
    44
  );

  pdf.setFontSize(10);

  pdf.setFont(
    "helvetica",
    "normal"
  );

  pdf.text(
    "Website Development • Applications • Automation",
    136,
    62
  );

  pdf.setTextColor(
    ...COLORS.subText
  );

  pdf.text(
    getClientName(
      client
    ),
    136,
    82
  );

  if (project?.title) {
    pdf.text(
      safeString(
        project.title
      ),
      136,
      96
    );
  }

  pdf.setTextColor(
    ...COLORS.black
  );

  pdf.setFont(
    "helvetica",
    "bold"
  );

  pdf.setFontSize(20);

  pdf.text(
    safeString(
      document.title
    ),
    PAGE_MARGIN,
    148
  );

  pdf.setFontSize(9);

  pdf.setFont(
    "helvetica",
    "normal"
  );

  pdf.setTextColor(
    ...COLORS.muted
  );

  const documentReference =
    document.document_number ||
    `Document ${document.id}`;

  pdf.text(
    safeString(
      documentReference
    ),
    PAGE_MARGIN,
    166
  );

  pdf.text(
    formatLabel(
      document.document_type
    ),
    PAGE_WIDTH -
      PAGE_MARGIN,
    166,
    {
      align: "right",
    }
  );

  pdf.setDrawColor(
    ...COLORS.border
  );

  pdf.line(
    PAGE_MARGIN,
    180,
    PAGE_WIDTH -
      PAGE_MARGIN,
    180
  );

  return 200;
}

function addSectionHeading(
  pdf,
  manager,
  title,
  onNewPage,
  minimumFollowingHeight = 42
) {
  /*
   * Keep every section heading with at least a useful portion of
   * the content that follows it. This prevents a heading from
   * being stranded at the bottom of a page while its paragraph,
   * labels, table, or signature content starts on the next page.
   */
  manager.ensureSpace(
    36 + Math.max(0, minimumFollowingHeight),
    onNewPage
  );

  const y =
    manager.getY();

  pdf.setFillColor(
    ...COLORS.light
  );

  pdf.roundedRect(
    PAGE_MARGIN,
    y,
    CONTENT_WIDTH,
    25,
    4,
    4,
    "F"
  );

  pdf.setFont(
    "helvetica",
    "bold"
  );

  pdf.setFontSize(11);

  pdf.setTextColor(
    ...COLORS.navy
  );

  pdf.text(
    safeString(title),
    PAGE_MARGIN + 10,
    y + 17
  );

  manager.move(36);
}

function addParagraph(
  pdf,
  manager,
  text,
  options = {}
) {
  const {
    fontSize = 10,
    bold = false,
    color =
      COLORS.black,
    indent = 0,
    spacingAfter = 12,
    onNewPage,
  } = options;

  const lines =
    splitText(
      pdf,
      text,
      CONTENT_WIDTH -
        indent
    );

  if (
    lines.length === 0
  ) {
    return;
  }

  const lineHeight =
    fontSize * 1.45;

  const requiredHeight =
    lines.length *
      lineHeight +
    spacingAfter;

  manager.ensureSpace(
    requiredHeight,
    onNewPage
  );

  pdf.setFont(
    "helvetica",
    bold
      ? "bold"
      : "normal"
  );

  pdf.setFontSize(
    fontSize
  );

  pdf.setTextColor(
    ...color
  );

  pdf.text(
    lines,
    PAGE_MARGIN + indent,
    manager.getY()
  );

  manager.move(
    requiredHeight
  );
}

function addLabelValue(
  pdf,
  manager,
  label,
  value,
  onNewPage
) {
  const cleanValue =
    cleanText(value) || "—";

  manager.ensureSpace(
    24,
    onNewPage
  );

  const y =
    manager.getY();

  pdf.setFont(
    "helvetica",
    "bold"
  );

  pdf.setFontSize(9);

  pdf.setTextColor(
    ...COLORS.muted
  );

  pdf.text(
    `${label}:`,
    PAGE_MARGIN,
    y
  );

  pdf.setFont(
    "helvetica",
    "normal"
  );

  pdf.setTextColor(
    ...COLORS.black
  );

  pdf.text(
    cleanValue,
    PAGE_MARGIN + 108,
    y
  );

  manager.move(20);
}

function addTotalsBox(
  pdf,
  manager,
  totals,
  onNewPage
) {
  const entries =
    totals.filter(
      (entry) =>
        (
          entry.value !==
            undefined &&
          entry.value !==
            null
        ) ||
        (
          entry.formattedValue !==
            undefined &&
          entry.formattedValue !==
            null
        )
    );

  const boxHeight =
    entries.length *
      22 +
    18;

  manager.ensureSpace(
    boxHeight + 10,
    onNewPage
  );

  const boxWidth =
    250;

  const boxX =
    PAGE_WIDTH -
    PAGE_MARGIN -
    boxWidth;

  const boxY =
    manager.getY();

  pdf.setFillColor(
    ...COLORS.light
  );

  pdf.setDrawColor(
    ...COLORS.border
  );

  pdf.roundedRect(
    boxX,
    boxY,
    boxWidth,
    boxHeight,
    5,
    5,
    "FD"
  );

  let rowY =
    boxY + 18;

  entries.forEach(
    (entry, index) => {
      const isLast =
        index ===
        entries.length - 1;

      const emphasize =
        Boolean(
          entry.emphasize
        ) || isLast;

      pdf.setFont(
        "helvetica",
        emphasize
          ? "bold"
          : "normal"
      );

      pdf.setFontSize(
        emphasize
          ? 10
          : 9
      );

      pdf.setTextColor(
        ...(
          emphasize
            ? COLORS.navy
            : COLORS.muted
        )
      );

      pdf.text(
        safeString(
          entry.label
        ),
        boxX + 12,
        rowY
      );

      pdf.text(
        formatTotalValue(
          entry
        ),
        boxX +
          boxWidth -
          12,
        rowY,
        {
          align: "right",
        }
      );

      rowY += 22;
    }
  );

  manager.move(
    boxHeight + 18
  );
}

function addItemsTable(
  pdf,
  manager,
  items,
  onNewPage
) {
  const columns = {
    description: {
      x: PAGE_MARGIN,
      width: 266,
    },

    quantity: {
      x:
        PAGE_MARGIN +
        276,
      width: 55,
    },

    unitPrice: {
      x:
        PAGE_MARGIN +
        340,
      width: 80,
    },

    total: {
      x:
        PAGE_MARGIN +
        430,
      width: 86,
    },
  };

  const drawHeader =
    () => {
      manager.ensureSpace(
        28
      );

      const y =
        manager.getY();

      pdf.setFillColor(
        ...COLORS.navy
      );

      pdf.rect(
        PAGE_MARGIN,
        y,
        CONTENT_WIDTH,
        24,
        "F"
      );

      pdf.setTextColor(
        ...COLORS.white
      );

      pdf.setFont(
        "helvetica",
        "bold"
      );

      pdf.setFontSize(8);

      pdf.text(
        "ITEM",
        columns.description.x +
          7,
        y + 16
      );

      pdf.text(
        "QTY",
        columns.quantity.x +
          7,
        y + 16
      );

      pdf.text(
        "UNIT PRICE",
        columns.unitPrice.x +
          7,
        y + 16
      );

      pdf.text(
        "TOTAL",
        columns.total.x +
          columns.total.width -
          7,
        y + 16,
        {
          align: "right",
        }
      );

      manager.move(24);
    };

  const newPageHeader =
    () => {
      drawHeader();
    };

  drawHeader();

  items.forEach(
    (item, index) => {
      const title =
        cleanText(
          item.title
        ) || "Item";

      const description =
        cleanText(
          item.description
        );

      const titleLines =
        splitText(
          pdf,
          title,
          columns.description
            .width - 14
        );

      const descriptionLines =
        description
          ? splitText(
              pdf,
              description,
              columns.description
                .width - 14
            )
          : [];

      const rowHeight =
        Math.max(
          40,
          12 +
            titleLines.length *
              12 +
            descriptionLines.length *
              10
        );

      manager.ensureSpace(
        rowHeight,
        newPageHeader
      );

      const rowY =
        manager.getY();

      if (
        index % 2 === 1
      ) {
        pdf.setFillColor(
          249,
          251,
          253
        );

        pdf.rect(
          PAGE_MARGIN,
          rowY,
          CONTENT_WIDTH,
          rowHeight,
          "F"
        );
      }

      pdf.setDrawColor(
        ...COLORS.border
      );

      pdf.line(
        PAGE_MARGIN,
        rowY +
          rowHeight,
        PAGE_WIDTH -
          PAGE_MARGIN,
        rowY +
          rowHeight
      );

      pdf.setTextColor(
        ...COLORS.black
      );

      pdf.setFont(
        "helvetica",
        "bold"
      );

      pdf.setFontSize(9);

      pdf.text(
        titleLines,
        columns.description.x +
          7,
        rowY + 14
      );

      if (
        descriptionLines.length >
        0
      ) {
        pdf.setFont(
          "helvetica",
          "normal"
        );

        pdf.setFontSize(8);

        pdf.setTextColor(
          ...COLORS.muted
        );

        pdf.text(
          descriptionLines,
          columns.description.x +
            7,
          rowY +
            14 +
            titleLines.length *
              12
        );
      }

      pdf.setFont(
        "helvetica",
        "normal"
      );

      pdf.setFontSize(9);

      pdf.setTextColor(
        ...COLORS.black
      );

      pdf.text(
        safeString(
          item.quantity || 0
        ),
        columns.quantity.x +
          7,
        rowY + 16
      );

      pdf.text(
        formatCurrency(
          item.unit_price
        ),
        columns.unitPrice.x +
          7,
        rowY + 16
      );

      pdf.text(
        formatCurrency(
          item.line_total
        ),
        columns.total.x +
          columns.total.width -
          7,
        rowY + 16,
        {
          align: "right",
        }
      );

      manager.move(
        rowHeight
      );
    }
  );

  manager.move(14);
}


function addAgreementSection(
  pdf,
  manager,
  title,
  content,
  onNewPage
) {
  const cleanedContent =
    cleanText(content);

  if (!cleanedContent) {
    return;
  }

  const paragraphs =
    cleanedContent
      .split(/\n{2,}/)
      .map((paragraph) =>
        paragraph.trim()
      )
      .filter(Boolean);

  const firstParagraph =
    paragraphs[0] || "";

  const firstParagraphLines =
    splitText(
      pdf,
      firstParagraph,
      CONTENT_WIDTH
    );

  const firstParagraphHeight =
    Math.max(
      28,
      Math.min(
        firstParagraphLines.length,
        4
      ) * 14.5 + 12
    );

  addSectionHeading(
    pdf,
    manager,
    title,
    onNewPage,
    firstParagraphHeight
  );

  paragraphs.forEach(
    (paragraph) => {
      addParagraph(
        pdf,
        manager,
        paragraph,
        {
          onNewPage,
        }
      );
    }
  );
}

function addSignatureBlock(
  pdf,
  manager,
  details,
  client,
  onNewPage
) {
  manager.ensureSpace(
    190,
    onNewPage
  );

  addSectionHeading(
    pdf,
    manager,
    "Acceptance and Signatures",
    onNewPage
  );

  addParagraph(
    pdf,
    manager,
    "By signing below, both parties acknowledge that they have reviewed and agree to the terms of this agreement.",
    {
      fontSize: 9,
      color:
        COLORS.muted,
      spacingAfter: 24,
      onNewPage,
    }
  );

  const leftX =
    PAGE_MARGIN;

  const rightX =
    PAGE_MARGIN +
    CONTENT_WIDTH / 2 +
    14;

  const columnWidth =
    CONTENT_WIDTH / 2 -
    14;

  const providerName =
    firstAvailableText(
      details.provider_name,
      "Kenneth L. Powell"
    );

  const providerBusiness =
    firstAvailableText(
      details.provider_business,
      "Avwakin Development"
    );

  const clientSignerName =
    firstAvailableText(
      details.client_signer_name,
      getClientName(client)
    );

  const clientSignerTitle =
    firstAvailableText(
      details.client_signer_title
    );

  const startY =
    manager.getY();

  pdf.setFont(
    "helvetica",
    "bold"
  );

  pdf.setFontSize(10);

  pdf.setTextColor(
    ...COLORS.navy
  );

  pdf.text(
    "SERVICE PROVIDER",
    leftX,
    startY
  );

  pdf.text(
    "CLIENT",
    rightX,
    startY
  );

  const signatureY =
    startY + 52;

  pdf.setDrawColor(
    ...COLORS.muted
  );

  pdf.line(
    leftX,
    signatureY,
    leftX + columnWidth,
    signatureY
  );

  pdf.line(
    rightX,
    signatureY,
    rightX + columnWidth,
    signatureY
  );

  pdf.setFont(
    "helvetica",
    "normal"
  );

  pdf.setFontSize(8);

  pdf.setTextColor(
    ...COLORS.muted
  );

  pdf.text(
    "Signature",
    leftX,
    signatureY + 12
  );

  pdf.text(
    "Signature",
    rightX,
    signatureY + 12
  );

  pdf.setFont(
    "helvetica",
    "bold"
  );

  pdf.setFontSize(9);

  pdf.setTextColor(
    ...COLORS.black
  );

  pdf.text(
    providerName,
    leftX,
    signatureY + 34
  );

  pdf.text(
    clientSignerName,
    rightX,
    signatureY + 34
  );

  pdf.setFont(
    "helvetica",
    "normal"
  );

  pdf.setFontSize(8);

  pdf.setTextColor(
    ...COLORS.muted
  );

  pdf.text(
    providerBusiness,
    leftX,
    signatureY + 47
  );

  if (clientSignerTitle) {
    pdf.text(
      clientSignerTitle,
      rightX,
      signatureY + 47
    );
  }

  const dateLineY =
    signatureY + 82;

  pdf.setDrawColor(
    ...COLORS.muted
  );

  pdf.line(
    leftX,
    dateLineY,
    leftX + columnWidth,
    dateLineY
  );

  pdf.line(
    rightX,
    dateLineY,
    rightX + columnWidth,
    dateLineY
  );

  pdf.setFontSize(8);

  pdf.setTextColor(
    ...COLORS.muted
  );

  pdf.text(
    "Date",
    leftX,
    dateLineY + 12
  );

  pdf.text(
    "Date",
    rightX,
    dateLineY + 12
  );

  manager.setY(
    dateLineY + 34
  );
}

function addAgreementContent(
  pdf,
  manager,
  document,
  client,
  project,
  proposal,
  template,
  onNewPage
) {
  const details =
    parseDocumentDetails(
      document.document_details
    );

  const effectiveDate =
    firstAvailableText(
      details.effective_date
    );

  addSectionHeading(
    pdf,
    manager,
    "Agreement Information",
    onNewPage
  );

  addLabelValue(
    pdf,
    manager,
    "Effective Date",
    formatDate(
      effectiveDate
    ),
    onNewPage
  );

  addLabelValue(
    pdf,
    manager,
    "Client",
    getClientName(
      client
    ),
    onNewPage
  );

  addLabelValue(
    pdf,
    manager,
    "Project",
    firstAvailableText(
      project?.title,
      "No linked project"
    ),
    onNewPage
  );

  if (
    proposal?.proposal_number
  ) {
    addLabelValue(
      pdf,
      manager,
      "Linked Proposal",
      proposal.proposal_number,
      onNewPage
    );
  }

  const introduction =
    firstAvailableText(
      details.introduction,
      template?.header_content,
      `This Website Development Agreement is entered into between Avwakin Development and ${getClientName(
        client
      )}. It defines the services, responsibilities, payment obligations, ownership terms, and other conditions governing the project.`
    );

  addAgreementSection(
    pdf,
    manager,
    "Purpose of Agreement",
    introduction,
    onNewPage
  );

  addAgreementSection(
    pdf,
    manager,
    "Project Scope",
    firstAvailableText(
      details.project_scope,
      proposal?.scope_summary
    ),
    onNewPage
  );

  addAgreementSection(
    pdf,
    manager,
    "Project Timeline",
    firstAvailableText(
      details.project_timeline,
      proposal?.estimated_timeline
    ),
    onNewPage
  );

  addAgreementSection(
    pdf,
    manager,
    "Payment Terms",
    details.payment_terms,
    onNewPage
  );

  addAgreementSection(
    pdf,
    manager,
    "Deposit Terms",
    details.deposit_terms,
    onNewPage
  );

  addAgreementSection(
    pdf,
    manager,
    "Revisions",
    details.revision_allowance,
    onNewPage
  );

  addAgreementSection(
    pdf,
    manager,
    "Change Requests",
    details.change_request_terms,
    onNewPage
  );

  addAgreementSection(
    pdf,
    manager,
    "Client Responsibilities",
    details.client_responsibilities,
    onNewPage
  );

  addAgreementSection(
    pdf,
    manager,
    "Ownership and Intellectual Property",
    details.ownership_terms,
    onNewPage
  );

  addAgreementSection(
    pdf,
    manager,
    "Third-Party Services",
    details.third_party_services,
    onNewPage
  );

  addAgreementSection(
    pdf,
    manager,
    "Hosting and Maintenance",
    details.hosting_maintenance,
    onNewPage
  );

  addAgreementSection(
    pdf,
    manager,
    "Cancellation and Termination",
    details.cancellation_terms,
    onNewPage
  );

  addAgreementSection(
    pdf,
    manager,
    "Limitation of Liability",
    details.liability_terms,
    onNewPage
  );

  const additionalTerms =
    firstAvailableText(
      details.additional_terms,
      template?.default_terms
    );

  addAgreementSection(
    pdf,
    manager,
    "Additional Terms",
    additionalTerms,
    onNewPage
  );

  if (
    !cleanText(
      details.project_scope
    ) &&
    !cleanText(
      details.payment_terms
    ) &&
    !cleanText(
      details.deposit_terms
    ) &&
    cleanText(
      document.generated_content
    )
  ) {
    addAgreementSection(
      pdf,
      manager,
      "Legacy Agreement Content",
      document.generated_content,
      onNewPage
    );
  }

  if (
    cleanText(
      template?.footer_content
    )
  ) {
    addAgreementSection(
      pdf,
      manager,
      "Closing Information",
      template.footer_content,
      onNewPage
    );
  }

  addSignatureBlock(
    pdf,
    manager,
    details,
    client,
    onNewPage
  );
}


function addScopeApprovalBlock(
  pdf,
  manager,
  details,
  client,
  onNewPage
) {
  manager.ensureSpace(
    190,
    onNewPage
  );

  addSectionHeading(
    pdf,
    manager,
    "Approval",
    onNewPage
  );

  addParagraph(
    pdf,
    manager,
    "By signing below, both parties confirm that this Scope of Work accurately describes the approved project work and may be changed only through an approved written change request.",
    {
      fontSize: 9,
      color:
        COLORS.muted,
      spacingAfter: 24,
      onNewPage,
    }
  );

  const leftX =
    PAGE_MARGIN;

  const rightX =
    PAGE_MARGIN +
    CONTENT_WIDTH / 2 +
    14;

  const columnWidth =
    CONTENT_WIDTH / 2 -
    14;

  const providerName =
    firstAvailableText(
      details.provider_name,
      "Kenneth L. Powell"
    );

  const providerBusiness =
    firstAvailableText(
      details.provider_business,
      "Avwakin Development"
    );

  const clientApproverName =
    firstAvailableText(
      details.client_approver_name,
      getClientName(client)
    );

  const clientApproverTitle =
    firstAvailableText(
      details.client_approver_title
    );

  const startY =
    manager.getY();

  pdf.setFont(
    "helvetica",
    "bold"
  );

  pdf.setFontSize(10);

  pdf.setTextColor(
    ...COLORS.navy
  );

  pdf.text(
    "SERVICE PROVIDER",
    leftX,
    startY
  );

  pdf.text(
    "CLIENT APPROVAL",
    rightX,
    startY
  );

  const signatureY =
    startY + 52;

  pdf.setDrawColor(
    ...COLORS.muted
  );

  pdf.line(
    leftX,
    signatureY,
    leftX + columnWidth,
    signatureY
  );

  pdf.line(
    rightX,
    signatureY,
    rightX + columnWidth,
    signatureY
  );

  pdf.setFont(
    "helvetica",
    "normal"
  );

  pdf.setFontSize(8);

  pdf.setTextColor(
    ...COLORS.muted
  );

  pdf.text(
    "Signature",
    leftX,
    signatureY + 12
  );

  pdf.text(
    "Signature",
    rightX,
    signatureY + 12
  );

  pdf.setFont(
    "helvetica",
    "bold"
  );

  pdf.setFontSize(9);

  pdf.setTextColor(
    ...COLORS.black
  );

  pdf.text(
    providerName,
    leftX,
    signatureY + 34
  );

  pdf.text(
    clientApproverName,
    rightX,
    signatureY + 34
  );

  pdf.setFont(
    "helvetica",
    "normal"
  );

  pdf.setFontSize(8);

  pdf.setTextColor(
    ...COLORS.muted
  );

  pdf.text(
    providerBusiness,
    leftX,
    signatureY + 47
  );

  if (clientApproverTitle) {
    pdf.text(
      clientApproverTitle,
      rightX,
      signatureY + 47
    );
  }

  const dateLineY =
    signatureY + 82;

  pdf.line(
    leftX,
    dateLineY,
    leftX + columnWidth,
    dateLineY
  );

  pdf.line(
    rightX,
    dateLineY,
    rightX + columnWidth,
    dateLineY
  );

  pdf.text(
    "Date",
    leftX,
    dateLineY + 12
  );

  pdf.text(
    "Date",
    rightX,
    dateLineY + 12
  );

  manager.setY(
    dateLineY + 34
  );
}

function addScopeOfWorkContent(
  pdf,
  manager,
  document,
  client,
  project,
  proposal,
  template,
  onNewPage
) {
  const details =
    parseDocumentDetails(
      document.document_details
    );

  addSectionHeading(
    pdf,
    manager,
    "Scope Information",
    onNewPage
  );

  addLabelValue(
    pdf,
    manager,
    "Effective Date",
    formatDate(
      details.effective_date
    ),
    onNewPage
  );

  addLabelValue(
    pdf,
    manager,
    "Client",
    getClientName(
      client
    ),
    onNewPage
  );

  addLabelValue(
    pdf,
    manager,
    "Project",
    firstAvailableText(
      project?.title,
      "No linked project"
    ),
    onNewPage
  );

  if (
    proposal?.proposal_number
  ) {
    addLabelValue(
      pdf,
      manager,
      "Linked Proposal",
      proposal.proposal_number,
      onNewPage
    );
  }

  addAgreementSection(
    pdf,
    manager,
    "Project Objective",
    details.project_objective,
    onNewPage
  );

  addAgreementSection(
    pdf,
    manager,
    "Project Overview",
    firstAvailableText(
      details.project_overview,
      proposal?.scope_summary,
      template?.header_content
    ),
    onNewPage
  );

  addAgreementSection(
    pdf,
    manager,
    "Deliverables",
    details.deliverables,
    onNewPage
  );

  addAgreementSection(
    pdf,
    manager,
    "Included Services",
    details.included_services,
    onNewPage
  );

  addAgreementSection(
    pdf,
    manager,
    "Excluded Services",
    details.excluded_services,
    onNewPage
  );

  addAgreementSection(
    pdf,
    manager,
    "Milestones",
    details.milestones,
    onNewPage
  );

  addAgreementSection(
    pdf,
    manager,
    "Estimated Timeline",
    firstAvailableText(
      details.estimated_timeline,
      proposal?.estimated_timeline
    ),
    onNewPage
  );

  addAgreementSection(
    pdf,
    manager,
    "Client Responsibilities",
    details.client_responsibilities,
    onNewPage
  );

  addAgreementSection(
    pdf,
    manager,
    "Avwakin Development Responsibilities",
    details.provider_responsibilities,
    onNewPage
  );

  addAgreementSection(
    pdf,
    manager,
    "Required Client Materials",
    details.required_client_materials,
    onNewPage
  );

  addAgreementSection(
    pdf,
    manager,
    "Dependencies and Assumptions",
    details.dependencies_assumptions,
    onNewPage
  );

  addAgreementSection(
    pdf,
    manager,
    "Acceptance Criteria",
    details.acceptance_criteria,
    onNewPage
  );

  addAgreementSection(
    pdf,
    manager,
    "Revision Allowance",
    details.revision_allowance,
    onNewPage
  );

  addAgreementSection(
    pdf,
    manager,
    "Change Request Process",
    details.change_request_process,
    onNewPage
  );

  addAgreementSection(
    pdf,
    manager,
    "Completion Requirements",
    details.completion_requirements,
    onNewPage
  );

  addAgreementSection(
    pdf,
    manager,
    "Additional Terms",
    firstAvailableText(
      details.additional_terms,
      template?.default_terms
    ),
    onNewPage
  );

  if (
    cleanText(
      template?.footer_content
    )
  ) {
    addAgreementSection(
      pdf,
      manager,
      "Additional Information",
      template.footer_content,
      onNewPage
    );
  }

  addScopeApprovalBlock(
    pdf,
    manager,
    details,
    client,
    onNewPage
  );
}


function addContractContent(
  pdf,
  manager,
  document,
  client,
  project,
  proposal,
  template,
  onNewPage
) {
  const details =
    parseDocumentDetails(
      document.document_details
    );

  addSectionHeading(
    pdf,
    manager,
    "Contract Information",
    onNewPage
  );

  addLabelValue(
    pdf,
    manager,
    "Effective Date",
    formatDate(
      details.effective_date
    ),
    onNewPage
  );

  addLabelValue(
    pdf,
    manager,
    "Start Date",
    formatDate(
      details.start_date
    ),
    onNewPage
  );

  addLabelValue(
    pdf,
    manager,
    "End Date",
    formatDate(
      details.end_date
    ),
    onNewPage
  );

  addLabelValue(
    pdf,
    manager,
    "Client",
    getClientName(client),
    onNewPage
  );

  addLabelValue(
    pdf,
    manager,
    "Project",
    firstAvailableText(
      project?.title,
      "No linked project"
    ),
    onNewPage
  );

  if (
    proposal?.proposal_number
  ) {
    addLabelValue(
      pdf,
      manager,
      "Linked Proposal",
      proposal.proposal_number,
      onNewPage
    );
  }

  const introduction =
    firstAvailableText(
      details.introduction,
      template?.header_content,
      `This Development Services Contract is entered into between Avwakin Development and ${getClientName(
        client
      )}. The parties agree to the following terms for the services described below.`
    );

  addAgreementSection(
    pdf,
    manager,
    "Contract Purpose",
    introduction,
    onNewPage
  );

  const sections = [
    [
      "Services",
      firstAvailableText(
        details.services,
        proposal?.scope_summary
      ),
    ],
    [
      "Compensation",
      details.compensation,
    ],
    [
      "Payment Schedule",
      details.payment_schedule,
    ],
    [
      "Avwakin Development Responsibilities",
      details.provider_responsibilities,
    ],
    [
      "Client Responsibilities",
      details.client_responsibilities,
    ],
    [
      "Confidentiality",
      details.confidentiality,
    ],
    [
      "Intellectual Property",
      details.intellectual_property,
    ],
    [
      "Third-Party Services",
      details.third_party_services,
    ],
    [
      "Warranties",
      details.warranties,
    ],
    [
      "Limitation of Liability",
      details.limitation_of_liability,
    ],
    [
      "Termination",
      details.termination,
    ],
    [
      "Dispute Resolution",
      details.dispute_resolution,
    ],
    [
      "Governing Law",
      details.governing_law,
    ],
    [
      "Entire Agreement",
      details.entire_agreement,
    ],
    [
      "Additional Terms",
      firstAvailableText(
        details.additional_terms,
        template?.default_terms
      ),
    ],
  ];

  sections.forEach(
    ([title, content]) => {
      addAgreementSection(
        pdf,
        manager,
        title,
        content,
        onNewPage
      );
    }
  );

  if (
    cleanText(
      template?.footer_content
    )
  ) {
    addAgreementSection(
      pdf,
      manager,
      "Closing Information",
      template.footer_content,
      onNewPage
    );
  }

  addSignatureBlock(
    pdf,
    manager,
    details,
    client,
    onNewPage
  );
}


function addStructuredSection(
  pdf,
  manager,
  title,
  content,
  onNewPage
) {
  const cleaned =
    cleanText(content);

  if (!cleaned) {
    return;
  }

  const lines =
    splitText(
      pdf,
      cleaned,
      CONTENT_WIDTH
    );

  const firstContentHeight =
    Math.max(
      28,
      Math.min(
        lines.length,
        4
      ) * 14.5 + 12
    );

  addSectionHeading(
    pdf,
    manager,
    title,
    onNewPage,
    firstContentHeight
  );

  addParagraph(
    pdf,
    manager,
    cleaned,
    { onNewPage }
  );
}

function addApprovalSignatureBlock(pdf, manager, details, client, heading, statement, onNewPage) {
  manager.ensureSpace(190, onNewPage);
  addSectionHeading(pdf, manager, heading, onNewPage);
  addParagraph(pdf, manager, statement, {
    fontSize: 9,
    color: COLORS.muted,
    spacingAfter: 24,
    onNewPage,
  });

  const leftX = PAGE_MARGIN;
  const rightX = PAGE_MARGIN + CONTENT_WIDTH / 2 + 14;
  const columnWidth = CONTENT_WIDTH / 2 - 14;
  const startY = manager.getY();
  const providerName = firstAvailableText(details.provider_name, "Kenneth L. Powell");
  const providerBusiness = firstAvailableText(details.provider_business, "Avwakin Development");
  const clientName = firstAvailableText(details.client_approver_name, details.client_signer_name, getClientName(client));
  const clientTitle = firstAvailableText(details.client_approver_title, details.client_signer_title);

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(10);
  pdf.setTextColor(...COLORS.navy);
  pdf.text("SERVICE PROVIDER", leftX, startY);
  pdf.text("CLIENT", rightX, startY);

  const signatureY = startY + 52;
  pdf.setDrawColor(...COLORS.muted);
  pdf.line(leftX, signatureY, leftX + columnWidth, signatureY);
  pdf.line(rightX, signatureY, rightX + columnWidth, signatureY);

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(8);
  pdf.setTextColor(...COLORS.muted);
  pdf.text("Signature", leftX, signatureY + 12);
  pdf.text("Signature", rightX, signatureY + 12);

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(9);
  pdf.setTextColor(...COLORS.black);
  pdf.text(providerName, leftX, signatureY + 34);
  pdf.text(clientName, rightX, signatureY + 34);

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(8);
  pdf.setTextColor(...COLORS.muted);
  pdf.text(providerBusiness, leftX, signatureY + 47);
  if (clientTitle) pdf.text(clientTitle, rightX, signatureY + 47);

  const dateY = signatureY + 82;
  pdf.line(leftX, dateY, leftX + columnWidth, dateY);
  pdf.line(rightX, dateY, rightX + columnWidth, dateY);
  pdf.text("Date", leftX, dateY + 12);
  pdf.text("Date", rightX, dateY + 12);
  manager.setY(dateY + 34);
}

function addProposalDocumentContent(pdf, manager, document, client, project, proposal, items, template, onNewPage) {
  const details = parseDocumentDetails(document.document_details);

  addProposalContent(pdf, manager, proposal, items, onNewPage);
  addStructuredSection(pdf, manager, "Executive Summary", details.executive_summary, onNewPage);
  addStructuredSection(pdf, manager, "Project Objectives", details.project_objectives, onNewPage);
  addStructuredSection(pdf, manager, "Deliverables", details.deliverables, onNewPage);
  addStructuredSection(pdf, manager, "Assumptions", details.assumptions, onNewPage);
  addStructuredSection(pdf, manager, "Exclusions", details.exclusions, onNewPage);
  addStructuredSection(pdf, manager, "Client Responsibilities", details.client_responsibilities, onNewPage);
  addStructuredSection(pdf, manager, "Avwakin Development Responsibilities", details.provider_responsibilities, onNewPage);
  addStructuredSection(pdf, manager, "Acceptance", details.acceptance_terms, onNewPage);
  addStructuredSection(pdf, manager, "Additional Terms", firstAvailableText(details.additional_terms, template?.default_terms), onNewPage);

  addApprovalSignatureBlock(
    pdf,
    manager,
    details,
    client,
    "Proposal Approval",
    "By signing below, the client confirms approval of this proposal and authorizes Avwakin Development to proceed according to its terms.",
    onNewPage
  );
}

function addReceiptContent(pdf, manager, document, client, project, invoice, onNewPage) {
  const details = parseDocumentDetails(document.document_details);
  const amountReceived = Number(details.amount_received) || 0;
  const remainingBalance = cleanText(details.remaining_balance)
    ? Number(details.remaining_balance) || 0
    : Number(invoice?.balance_due) || 0;

  addSectionHeading(pdf, manager, "Receipt Information", onNewPage);
  addLabelValue(pdf, manager, "Receipt Number", document.document_number, onNewPage);
  addLabelValue(pdf, manager, "Payment Date", formatDate(details.payment_date), onNewPage);
  addLabelValue(pdf, manager, "Client", getClientName(client), onNewPage);
  addLabelValue(pdf, manager, "Project", firstAvailableText(project?.title, "—"), onNewPage);
  if (invoice?.invoice_number) addLabelValue(pdf, manager, "Invoice Number", invoice.invoice_number, onNewPage);
  addLabelValue(pdf, manager, "Payment Method", firstAvailableText(formatLabel(details.payment_method), "—"), onNewPage);
  addLabelValue(pdf, manager, "Transaction Reference", firstAvailableText(details.transaction_reference, "—"), onNewPage);
  addStructuredSection(pdf, manager, "Payment Description", details.payment_description, onNewPage);
  addTotalsBox(pdf, manager, [
    { label: "Amount Received", value: amountReceived, emphasize: true },
    { label: "Remaining Balance", value: remainingBalance },
  ], onNewPage);
  addStructuredSection(pdf, manager, "Notes", details.additional_notes, onNewPage);
  addParagraph(pdf, manager, `Received by ${firstAvailableText(details.received_by, "Kenneth L. Powell")} on behalf of Avwakin Development.`, {
    fontSize: 9,
    color: COLORS.muted,
    onNewPage,
  });
}

function addCompletionContent(pdf, manager, document, client, project, proposal, invoice, onNewPage) {
  const details = parseDocumentDetails(document.document_details);

  addSectionHeading(pdf, manager, "Completion Information", onNewPage);
  addLabelValue(pdf, manager, "Completion Date", formatDate(details.completion_date), onNewPage);
  addLabelValue(pdf, manager, "Client", getClientName(client), onNewPage);
  addLabelValue(pdf, manager, "Project", firstAvailableText(project?.title, "—"), onNewPage);
  if (proposal?.proposal_number) addLabelValue(pdf, manager, "Proposal", proposal.proposal_number, onNewPage);
  if (invoice?.invoice_number) addLabelValue(pdf, manager, "Final Invoice", invoice.invoice_number, onNewPage);

  addStructuredSection(pdf, manager, "Completion Summary", details.completion_summary, onNewPage);
  addStructuredSection(pdf, manager, "Delivered Items", details.delivered_items, onNewPage);
  addStructuredSection(pdf, manager, "Outstanding Items", details.outstanding_items, onNewPage);
  addStructuredSection(pdf, manager, "Credentials and Access", details.credentials_transferred, onNewPage);
  addStructuredSection(pdf, manager, "Training and Handoff", details.training_provided, onNewPage);
  addStructuredSection(pdf, manager, "Support Period", details.support_period, onNewPage);
  addStructuredSection(pdf, manager, "Warranty Period", details.warranty_period, onNewPage);
  addStructuredSection(pdf, manager, "Final Balance", details.final_balance_terms, onNewPage);
  addStructuredSection(pdf, manager, "Additional Notes", details.additional_notes, onNewPage);

  addApprovalSignatureBlock(
    pdf,
    manager,
    details,
    client,
    "Project Acceptance",
    firstAvailableText(details.client_acceptance, "By signing below, the client confirms receipt of the approved deliverables and acknowledges project completion."),
    onNewPage
  );
}

function addOtherDocumentContent(pdf, manager, document, client, project, onNewPage) {
  const details = parseDocumentDetails(document.document_details);

  addSectionHeading(pdf, manager, "Document Information", onNewPage);
  addLabelValue(pdf, manager, "Effective Date", formatDate(details.effective_date), onNewPage);
  addLabelValue(pdf, manager, "Prepared For", firstAvailableText(details.prepared_for, getClientName(client)), onNewPage);
  addLabelValue(pdf, manager, "Prepared By", firstAvailableText(details.prepared_by, "Kenneth L. Powell"), onNewPage);
  addLabelValue(pdf, manager, "Project", firstAvailableText(project?.title, "—"), onNewPage);
  addStructuredSection(pdf, manager, "Purpose", details.purpose, onNewPage);
  addStructuredSection(pdf, manager, "Summary", details.summary, onNewPage);
  addStructuredSection(pdf, manager, "Document Details", firstAvailableText(details.document_body, document.generated_content), onNewPage);
  addStructuredSection(pdf, manager, "Action Items", details.action_items, onNewPage);
  addStructuredSection(pdf, manager, "References", details.references, onNewPage);
  addStructuredSection(pdf, manager, "Additional Notes", details.additional_notes, onNewPage);
}

function addGenericDocumentContent(
  pdf,
  manager,
  document,
  template,
  onNewPage
) {
  if (
    template?.header_content
  ) {
    addSectionHeading(
      pdf,
      manager,
      "Introduction",
      onNewPage
    );

    addParagraph(
      pdf,
      manager,
      template.header_content,
      {
        onNewPage,
      }
    );
  }

  addSectionHeading(
    pdf,
    manager,
    "Document Details",
    onNewPage
  );

  const content =
    document.generated_content ||
    template?.body_content ||
    "No generated content has been added.";

  const paragraphs =
    safeString(content)
      .split(/\n{2,}/)
      .map((entry) =>
        entry.trim()
      )
      .filter(Boolean);

  paragraphs.forEach(
    (paragraph) => {
      addParagraph(
        pdf,
        manager,
        paragraph,
        {
          onNewPage,
        }
      );
    }
  );

  if (
    template?.default_terms
  ) {
    addSectionHeading(
      pdf,
      manager,
      "Terms",
      onNewPage
    );

    addParagraph(
      pdf,
      manager,
      template.default_terms,
      {
        onNewPage,
      }
    );
  }

  if (
    template?.footer_content
  ) {
    addSectionHeading(
      pdf,
      manager,
      "Additional Information",
      onNewPage
    );

    addParagraph(
      pdf,
      manager,
      template.footer_content,
      {
        onNewPage,
      }
    );
  }
}

function addProposalContent(
  pdf,
  manager,
  proposal,
  items,
  onNewPage
) {
  addSectionHeading(
    pdf,
    manager,
    "Proposal Information",
    onNewPage
  );

  addLabelValue(
    pdf,
    manager,
    "Proposal Number",
    proposal.proposal_number,
    onNewPage
  );

  addLabelValue(
    pdf,
    manager,
    "Expiration Date",
    formatDate(
      proposal.expiration_date
    ),
    onNewPage
  );

  addLabelValue(
    pdf,
    manager,
    "Estimated Timeline",
    proposal.estimated_timeline,
    onNewPage
  );

  addLabelValue(
    pdf,
    manager,
    "Deposit",
    `${Number(
      proposal.deposit_percentage ||
        0
    )}%`,
    onNewPage
  );

  if (
    proposal.introduction
  ) {
    addSectionHeading(
      pdf,
      manager,
      "Introduction",
      onNewPage
    );

    addParagraph(
      pdf,
      manager,
      proposal.introduction,
      {
        onNewPage,
      }
    );
  }

  if (
    proposal.scope_summary
  ) {
    addSectionHeading(
      pdf,
      manager,
      "Scope of Work",
      onNewPage
    );

    addParagraph(
      pdf,
      manager,
      proposal.scope_summary,
      {
        onNewPage,
      }
    );
  }

  addSectionHeading(
    pdf,
    manager,
    "Services and Pricing",
    onNewPage
  );

  if (
    items.length > 0
  ) {
    addItemsTable(
      pdf,
      manager,
      items,
      onNewPage
    );
  } else {
    addParagraph(
      pdf,
      manager,
      "No proposal items have been added.",
      {
        color:
          COLORS.muted,

        onNewPage,
      }
    );
  }

  addTotalsBox(
    pdf,
    manager,
    [
      {
        label:
          "Subtotal",

        value:
          proposal.subtotal,
      },

      {
        label:
          "Discount",

        value:
          proposal.discount_amount,
      },

      {
        label: "Tax",

        value:
          proposal.tax_amount,
      },

      {
        label: "Total",

        value:
          proposal.total_amount,
      },
    ],
    onNewPage
  );

  if (
    proposal.terms
  ) {
    addSectionHeading(
      pdf,
      manager,
      "Terms and Conditions",
      onNewPage
    );

    addParagraph(
      pdf,
      manager,
      proposal.terms,
      {
        onNewPage,
      }
    );
  }
}

function addInvoiceContent(
  pdf,
  manager,
  invoice,
  items,
  project,
  onNewPage
) {
  addSectionHeading(
    pdf,
    manager,
    "Invoice Information",
    onNewPage
  );

  addLabelValue(
    pdf,
    manager,
    "Invoice Number",
    invoice.invoice_number,
    onNewPage
  );

  addLabelValue(
    pdf,
    manager,
    "Invoice Type",
    formatLabel(
      invoice.invoice_type
    ),
    onNewPage
  );

  addLabelValue(
    pdf,
    manager,
    "Issue Date",
    formatDate(
      invoice.issue_date
    ),
    onNewPage
  );

  addLabelValue(
    pdf,
    manager,
    "Due Date",
    formatDate(
      invoice.due_date
    ),
    onNewPage
  );

  addSectionHeading(
    pdf,
    manager,
    "Invoice Items",
    onNewPage
  );

  if (
    items.length > 0
  ) {
    addItemsTable(
      pdf,
      manager,
      items,
      onNewPage
    );
  } else {
    addParagraph(
      pdf,
      manager,
      "No invoice items have been added.",
      {
        color:
          COLORS.muted,

        onNewPage,
      }
    );
  }

  const invoiceType =
    cleanText(
      invoice.invoice_type
    ).toLowerCase();

  const itemSubtotal =
    Number(
      sumItemTotals(
        items
      ).toFixed(2)
    );

  const storedProjectTotal =
    Number(
      project?.total_cost
    );

  const fullProjectAmount =
    storedProjectTotal > 0
      ? storedProjectTotal
      : itemSubtotal;

  const storedPercentage =
    normalizePercentage(
      project?.deposit_percentage
    );

  const calculatedPercentage =
    fullProjectAmount > 0
      ? normalizePercentage(
          (
            Number(
              invoice.subtotal
            ) /
            fullProjectAmount
          ) *
            100
        )
      : 0;

  const depositPercentage =
    storedPercentage > 0
      ? storedPercentage
      : calculatedPercentage;

  const depositSubtotal =
    Number(
      invoice.subtotal
    ) || 0;

  const storedRemaining =
    Number(
      project?.remaining_amount
    );

  const remainingProjectBalance =
    storedRemaining > 0
      ? storedRemaining
      : Math.max(
          0,
          fullProjectAmount -
            depositSubtotal
        );

  if (
    invoiceType ===
    "deposit"
  ) {
    addParagraph(
      pdf,
      manager,
      `This invoice requests a ${formatPercentage(
        depositPercentage
      )} deposit toward the full project amount shown in the line items above.`,
      {
        fontSize: 9,

        color:
          COLORS.muted,

        spacingAfter: 12,

        onNewPage,
      }
    );

    addTotalsBox(
      pdf,
      manager,
      [
        {
          label:
            "Full Project Amount",

          value:
            fullProjectAmount,
        },

        {
          label:
            "Deposit Percentage",

          formattedValue:
            formatPercentage(
              depositPercentage
            ),
        },

        {
          label:
            "Deposit Subtotal",

          value:
            invoice.subtotal,

          emphasize: true,
        },

        {
          label:
            "Discount",

          value:
            invoice.discount_amount,
        },

        {
          label: "Tax",

          value:
            invoice.tax_amount,
        },

        {
          label:
            "Total Deposit Due",

          value:
            invoice.total_amount,

          emphasize: true,
        },

        {
          label:
            "Amount Paid",

          value:
            invoice.amount_paid,
        },

        {
          label:
            "Balance Due",

          value:
            invoice.balance_due,

          emphasize: true,
        },

        {
          label:
            "Remaining Project Balance",

          value:
            remainingProjectBalance,
        },
      ],
      onNewPage
    );
  } else {
    addTotalsBox(
      pdf,
      manager,
      [
        {
          label:
            "Subtotal",

          value:
            invoice.subtotal,
        },

        {
          label:
            "Discount",

          value:
            invoice.discount_amount,
        },

        {
          label:
            "Tax",

          value:
            invoice.tax_amount,
        },

        {
          label:
            "Total",

          value:
            invoice.total_amount,
        },

        {
          label:
            "Amount Paid",

          value:
            invoice.amount_paid,
        },

        {
          label:
            "Balance Due",

          value:
            invoice.balance_due,
        },
      ],
      onNewPage
    );
  }

  if (
    invoice.payment_method
  ) {
    addSectionHeading(
      pdf,
      manager,
      "Payment Information",
      onNewPage
    );

    addLabelValue(
      pdf,
      manager,
      "Payment Method",
      formatLabel(
        invoice.payment_method
      ),
      onNewPage
    );
  }

  if (
    invoice.notes
  ) {
    addSectionHeading(
      pdf,
      manager,
      "Notes",
      onNewPage
    );

    addParagraph(
      pdf,
      manager,
      invoice.notes,
      {
        onNewPage,
      }
    );
  }
}

async function buildDevelopmentPdf({
  document,
  client,
  project,
  proposal,
  invoice,
  template,
  items,
}) {
  const pdf =
    new jsPDF({
      orientation:
        "portrait",

      unit: "pt",

      format: [
        PAGE_WIDTH,
        PAGE_HEIGHT,
      ],
    });

  const logoDataUrl =
    await imageUrlToDataUrl(
      developmentLogo
    );

  const manager =
    createPageManager(
      pdf
    );

  const addContinuationHeader =
    () => {
      pdf.setFont(
        "helvetica",
        "bold"
      );

      pdf.setFontSize(13);

      pdf.setTextColor(
        ...COLORS.navy
      );

      pdf.text(
        safeString(
          document.title
        ),
        PAGE_MARGIN,
        PAGE_MARGIN
      );

      pdf.setFont(
        "helvetica",
        "normal"
      );

      pdf.setFontSize(8);

      pdf.setTextColor(
        ...COLORS.muted
      );

      pdf.text(
        safeString(
          document.document_number ||
            ""
        ),
        PAGE_WIDTH -
          PAGE_MARGIN,
        PAGE_MARGIN,
        {
          align: "right",
        }
      );

      pdf.setDrawColor(
        ...COLORS.border
      );

      pdf.line(
        PAGE_MARGIN,
        PAGE_MARGIN + 12,
        PAGE_WIDTH -
          PAGE_MARGIN,
        PAGE_MARGIN + 12
      );

      manager.setY(
        PAGE_MARGIN + 34
      );
    };

  manager.setY(
    addDocumentHeader(
      pdf,
      logoDataUrl,
      document,
      client,
      project
    )
  );

  if (
    document.document_type ===
      "proposal" &&
    proposal
  ) {
    addProposalDocumentContent(
      pdf,
      manager,
      document,
      client,
      project,
      proposal,
      items,
      template,
      addContinuationHeader
    );
  } else if (
    document.document_type ===
      "invoice" &&
    invoice
  ) {
    addInvoiceContent(
      pdf,
      manager,
      invoice,
      items,
      project,
      addContinuationHeader
    );
  } else if (
    document.document_type ===
      "agreement"
  ) {
    addAgreementContent(
      pdf,
      manager,
      document,
      client,
      project,
      proposal,
      template,
      addContinuationHeader
    );
  } else if (
    document.document_type ===
      "scope_of_work"
  ) {
    addScopeOfWorkContent(
      pdf,
      manager,
      document,
      client,
      project,
      proposal,
      template,
      addContinuationHeader
    );
  } else if (
    document.document_type ===
      "contract"
  ) {
    addContractContent(
      pdf,
      manager,
      document,
      client,
      project,
      proposal,
      template,
      addContinuationHeader
    );
  } else if (document.document_type === "receipt") {
    addReceiptContent(
      pdf, manager, document, client, project, invoice, addContinuationHeader
    );
  } else if (document.document_type === "completion") {
    addCompletionContent(
      pdf, manager, document, client, project, proposal, invoice, addContinuationHeader
    );
  } else if (document.document_type === "other") {
    addOtherDocumentContent(
      pdf, manager, document, client, project, addContinuationHeader
    );
  } else {
    addGenericDocumentContent(
      pdf,
      manager,
      document,
      template,
      addContinuationHeader
    );
  }

  addAllPageFooters(
    pdf
  );

  return pdf;
}

export default function DevelopmentPdfPreview({
  document,
  client,
  project,
  proposal = null,
  invoice = null,
  template = null,
  items = [],
  onClose,
  onStored,
}) {
  const [
    pdfBlobUrl,
    setPdfBlobUrl,
  ] = useState("");

  const [
    pdfBase64,
    setPdfBase64,
  ] = useState("");

  const [
    fileName,
    setFileName,
  ] = useState("");

  const [
    isBuilding,
    setIsBuilding,
  ] = useState(true);

  const [
    isSaving,
    setIsSaving,
  ] = useState(false);

  const [
    error,
    setError,
  ] = useState("");

  const reference =
    useMemo(
      () =>
        document.document_number ||
        proposal?.proposal_number ||
        invoice?.invoice_number ||
        `development-document-${document.id}`,
      [
        document,
        proposal,
        invoice,
      ]
    );

  useEffect(() => {
    let currentObjectUrl =
      "";

    const generatePreview =
      async () => {
        setIsBuilding(true);
        setError("");

        try {
          const pdf =
            await buildDevelopmentPdf({
              document,
              client,
              project,
              proposal,
              invoice,
              template,
              items,
            });

          const arrayBuffer =
            pdf.output(
              "arraybuffer"
            );

          const blob =
            new Blob(
              [
                arrayBuffer,
              ],
              {
                type:
                  "application/pdf",
              }
            );

          currentObjectUrl =
            URL.createObjectURL(
              blob
            );

          setPdfBlobUrl(
            currentObjectUrl
          );

          setPdfBase64(
            arrayBufferToBase64(
              arrayBuffer
            )
          );

          setFileName(
            sanitizeFileName(
              reference
            )
          );
        } catch (
          generationError
        ) {
          console.error(
            "Development PDF generation failed:",
            generationError
          );

          setError(
            generationError.message ||
              "The PDF could not be generated."
          );
        } finally {
          setIsBuilding(
            false
          );
        }
      };

    generatePreview();

    return () => {
      if (
        currentObjectUrl
      ) {
        URL.revokeObjectURL(
          currentObjectUrl
        );
      }
    };
  }, [
    document,
    client,
    project,
    proposal,
    invoice,
    template,
    items,
    reference,
  ]);

  const savePdf =
    async () => {
      if (!pdfBase64) {
        setError(
          "The PDF has not finished generating."
        );

        return;
      }

      setIsSaving(true);
      setError("");

      try {
        const response =
          await Api.saveDevelopmentDocumentPdf(
            document.id,
            {
              file_name:
                fileName,

              pdf_base64:
                pdfBase64,
            }
          );

        if (
          typeof onStored ===
          "function"
        ) {
          await onStored(
            response
          );
        }
      } catch (
        requestError
      ) {
        setError(
          requestError.message ||
            "The PDF could not be saved."
        );
      } finally {
        setIsSaving(
          false
        );
      }
    };

  const downloadPreview =
    () => {
      if (!pdfBlobUrl) {
        return;
      }

      const link =
        window.document.createElement(
          "a"
        );

      link.href =
        pdfBlobUrl;

      link.download =
        fileName;

      window.document.body.appendChild(
        link
      );

      link.click();

      window.document.body.removeChild(
        link
      );
    };

  return (
    <div
      className="development-pdf-modal-backdrop"
      role="presentation"
    >
      <section
        className="development-pdf-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="development-pdf-title"
      >
        <div className="development-pdf-modal-heading">
          <div>
            <p className="development-admin-eyebrow">
              PDF preview
            </p>

            <h3 id="development-pdf-title">
              {document.title}
            </h3>

            <span>
              {fileName ||
                "Generating filename..."}
            </span>
          </div>

          <button
            type="button"
            className="development-pdf-close-button"
            onClick={onClose}
            disabled={
              isSaving
            }
            aria-label="Close PDF preview"
          >
            ×
          </button>
        </div>

        {error && (
          <div
            className="development-admin-alert error"
            role="alert"
          >
            {error}
          </div>
        )}

        <div className="development-pdf-preview-container">
          {isBuilding ? (
            <div className="development-pdf-loading">
              Generating PDF preview...
            </div>
          ) : pdfBlobUrl ? (
            <iframe
              title={`${document.title} PDF preview`}
              src={pdfBlobUrl}
            />
          ) : (
            <div className="development-pdf-loading">
              The PDF preview could not be loaded.
            </div>
          )}
        </div>

        <div className="development-pdf-modal-actions">
          <button
            type="button"
            className="development-admin-button secondary"
            onClick={onClose}
            disabled={
              isSaving
            }
          >
            Cancel
          </button>

          <button
            type="button"
            className="development-admin-button secondary"
            onClick={
              downloadPreview
            }
            disabled={
              isBuilding ||
              !pdfBlobUrl ||
              isSaving
            }
          >
            Download preview
          </button>

          <button
            type="button"
            className="development-admin-button primary"
            onClick={
              savePdf
            }
            disabled={
              isBuilding ||
              !pdfBase64 ||
              isSaving
            }
          >
            {isSaving
              ? "Saving PDF..."
              : document.pdf_file_url
                ? "Regenerate and save"
                : "Generate and save"}
          </button>
        </div>
      </section>
    </div>
  );
}